import { useCallback, useEffect, useState } from 'react';

type NativeBiometricPayload = {
  supported?: boolean;
  available?: boolean;
  enabled?: boolean;
  label?: string;
  message?: string;
  reason?: string;
};

type AndroidBiometricBridge = {
  getBiometricLockState?: () => string;
  requestBiometricLockToggle?: (enabled: boolean) => void;
};

declare global {
  interface Window {
    Bid4BabesAndroid?: AndroidBiometricBridge;
    webkit?: {
      messageHandlers?: {
        bid4babesNative?: {
          postMessage: (message: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const emptyState = {
  supported: false,
  available: false,
  enabled: false,
  label: 'App Lock',
  message: '',
};

const normalizeState = (payload?: NativeBiometricPayload) => ({
  supported: Boolean(payload?.supported),
  available: Boolean(payload?.available),
  enabled: Boolean(payload?.enabled),
  label: payload?.label || 'Face ID / Fingerprint',
  message: payload?.message || payload?.reason || '',
});

const getIOSBridge = () => window.webkit?.messageHandlers?.bid4babesNative;

const readAndroidState = (): NativeBiometricPayload | null => {
  const rawState = window.Bid4BabesAndroid?.getBiometricLockState?.();
  if (!rawState) return null;

  try {
    return JSON.parse(rawState) as NativeBiometricPayload;
  } catch {
    return null;
  }
};

const useNativeBiometricLock = (enabledInView: boolean) => {
  const [state, setState] = useState(emptyState);
  const [pending, setPending] = useState(false);

  const refresh = useCallback(() => {
    if (!enabledInView || typeof window === 'undefined') {
      setState(emptyState);
      return;
    }

    const androidState = readAndroidState();
    if (androidState) {
      setState(normalizeState(androidState));
      return;
    }

    const iosBridge = getIOSBridge();
    if (iosBridge) {
      iosBridge.postMessage({ action: 'getBiometricLockState' });
      return;
    }

    setState(emptyState);
  }, [enabledInView]);

  const setEnabled = useCallback((nextEnabled: boolean) => {
    if (!enabledInView || typeof window === 'undefined') {
      return false;
    }

    if (window.Bid4BabesAndroid?.requestBiometricLockToggle) {
      setPending(true);
      window.Bid4BabesAndroid.requestBiometricLockToggle(nextEnabled);
      return true;
    }

    const iosBridge = getIOSBridge();
    if (iosBridge) {
      setPending(true);
      iosBridge.postMessage({ action: 'setBiometricLockEnabled', enabled: nextEnabled });
      return true;
    }

    return false;
  }, [enabledInView]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleNativeState = (event: Event) => {
      const detail = (event as CustomEvent<NativeBiometricPayload>).detail;
      setState(normalizeState(detail));
      setPending(false);
    };

    window.addEventListener('bid4babes:biometric-state', handleNativeState);
    refresh();

    return () => {
      window.removeEventListener('bid4babes:biometric-state', handleNativeState);
    };
  }, [refresh]);

  return {
    ...state,
    pending,
    refresh,
    setEnabled,
  };
};

export default useNativeBiometricLock;
